import { IWorkspaceMemberPayload } from '@publishing-engine/domain/actions/v1';
import { ReportsRequestSchema } from '@publishing-engine/domain/actions/v1/reports';
import {
  IArticleType,
  IFormDraft,
  IJournalMember,
  IWorkspaceMembership,
  IReviewAdditionalInformation,
  ISavedFilters,
  ISubmissionDraft,
  ITags,
  IIntegrityCheckManualRunPayload,
  IIntegrityCheckManualRunResponse,
  IRoleType,
  exportConfigItem,
  IUADReportAcknowledgmentPayload,
  IEditorialFormField
} from '@publishing-engine/domain/models';
import {
  useMutation,
  useQueryClient,
  QueryClient
} from '@tanstack/react-query';
import {
  MY_SUBMISSIONS_TOTALS_QUERY_KEY,
  USER_ACTIVITY_TOTALS_QUERY_KEY
} from 'src/constants';
import { IRolePayload } from 'src/pages/Organization/Workspace/Settings/Roles/roleFormSchema';
import axiosInstance from './client';
import {
  IListResponse,
  IMyReviewsFilters,
  IMySubmissionFilter,
  IWorkspaceSubmissionFilter,
  checkCoReviewerEligibility
} from './queries';
import {
  CoReviewDeclineReasonRoute,
  DashboardSavedFilterRoute,
  FormDraftRoute,
  IAddWorkspaceMembershipResponse,
  IGetTransferNetworkConfigResponse,
  JournalMembershipRoute,
  JournalSettingRoute,
  OrganizationArticleTypesRoute,
  ReminderConfigsRouteForJournals,
  TransferNetworkConfigRoute,
  WorkspaceGrantOrgAdminRoute,
  WorkspaceRevertOrgAdminRoute,
  WorkspaceMembershipIdRoute,
  WorkspaceMembershipsRoute,
  WorkspaceMergeAccountsRoute,
  WorkspaceMergeAccountsUndoRoute,
  WorkspaceRolesRoute,
  WorkspaceSavedFilterRoute,
  WorkspaceSavedFiltersRoute,
  WorkspaceTagRoute,
  WorkspaceTagsRoute,
  JournalConfigurationBucketsRoute,
  JournalConfigurationDecisionTemplatesRoute,
  JournalConfigurationArticleChecklistRoute
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { IJournalSettingFormData } from 'src/pages/Organization/Workspace/Settings/Journals/[id]/Configure';
import { IFocusIssueFormData } from 'src/pages/Organization/Workspace/Settings/Journals/[id]/Configure/FocusIssueModal';
import { IJournalBoardMembers } from 'src/pages/Organization/Workspace/Settings/Journals/[id]/Configure/AddBoardMembersModal';
import { IInviteCoReviewerCommand } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/inviteCoReviewer';
import { ICoReviewerInviteFormPayload } from 'src/pages/Review/CoReviewFlow';
import { reminderInDaysSchema } from '@publishing-engine/domain/validation';
import {
  IRunImageGenerationCheckPayload,
  IRunImageGenerationCheckResponse
} from '@publishing-engine/domain/actions/v1';

import z from 'zod';
import { IEarlyAssessmentListFilters } from 'src/pages/MyTask/Assessments/hooks';
import { IAdjudicationsListFilters } from 'src/pages/MyTask/Adjudications/hooks';
import {
  IVendorExportJobPayload,
  ValidateManuscriptIdsPayload,
  ValidateManuscriptIdsResponse,
  VendorExportBulkRetryResponse,
  VendorExportRetryBulkExportPayload
} from '@publishing-engine/domain/models/vendor-exports';
import { ContactJournalPayload } from '@publishing-engine/domain/models/contact-publisher';
import { JournalConfigForm } from 'src/pages/Organization/Workspace/Settings/Journals/[id]/Configure/JournalConfig';
import { ISubmissionStepContentPayload } from 'src/pages/Organization/Workspace/Settings/Journals/[id]/Configure/SubmissionStepContentConfig/stepConfig';
import { ITagCategory } from 'src/pages/Organization/Workspace/Settings/ManageTags/CreateTagCategory';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import { ContactUsPayload } from '@publishing-engine/domain/models/contact-us';

type IAxiosMethod = 'post' | 'delete' | 'put' | 'patch';

const axiosMutationClient = <T>({
  url,
  payload,
  method = 'post',
  params
}: {
  url: string;
  payload?: unknown;
  method?: IAxiosMethod;
  params?: Record<string, string | boolean>;
}) =>
  axiosInstance
    .request<T>({ url, method, data: payload, params })
    .then(r => ({
      ...r.data,
      isSuccess: true as const
    }))
    .catch(e => ({
      message: e.response?.data,
      status: e.response?.status,
      isSuccess: false as const
    }));

export const useCreateWorkspaceRole = (workspaceId: string) => {
  const queryKey = ['workspace-roles', { workspaceId }];
  const queryClient = useQueryClient();

  const createWorkspaceRoleMutation = useMutation({
    mutationFn: (formData: IRolePayload) => {
      const { name, displayName, permissions } = formData;

      return axiosMutationClient({
        url: WorkspaceRolesRoute.build({ id: workspaceId }),
        payload: { name, displayName, permissions }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return createWorkspaceRoleMutation;
};

export const useDeleteWorkspaceRole = (
  workspaceId: string,
  roleName: string
) => {
  const queryKey = ['workspace-roles', { workspaceId }];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      axiosMutationClient({
        url: `/workspaces/${workspaceId}/roles/${roleName}`,
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });
};
export const useUpdateWorkspaceRole = (
  workspaceId: string,
  roleName: string
) => {
  const queryKey = ['workspace-roles', { workspaceId }];
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: IRolePayload) => {
      const { displayName, permissions } = formData;
      return axiosMutationClient({
        url: `/workspaces/${workspaceId}/roles/${roleName}`,
        method: 'put',
        payload: {
          displayName,
          permissions
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });
};

export const useUpdateJournalSettings = (submissionContextId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-context', submissionContextId];

  const mutation = useMutation({
    mutationFn: (
      formData:
        | IJournalSettingFormData
        | JournalConfigForm
        | ISubmissionStepContentPayload
        | { isActive: boolean }
    ) =>
      axiosMutationClient({
        url: JournalSettingRoute.build({ ctxId: submissionContextId }),
        payload: formData,
        method: 'patch'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
};

export const addJournalBoardMembers = (submissionContextId: string) => {
  const queryClient = useQueryClient();
  const queryKey = [
    'submission-context-board-members',
    { id: submissionContextId }
  ];

  const mutation = useMutation({
    mutationFn: (formData: IJournalBoardMembers) =>
      axiosMutationClient({
        url: `submission-contexts/${submissionContextId}/board-members`,
        payload: formData,
        method: 'post'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
};

export const removeJournalBoardMembers = (submissionContextId: string) => {
  const queryClient = useQueryClient();
  const queryKey = [
    'submission-context-board-members',
    { id: submissionContextId }
  ];

  const mutation = useMutation({
    mutationFn: (userId: string) => {
      return axiosMutationClient({
        url: `submission-contexts/${submissionContextId}/board-members/${userId}`,
        method: 'delete'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
};

export const removeUserFromWorkspace = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-memberships', { workspaceId }];

  const removeUserFromOrganizationMutation = useMutation({
    mutationFn: (membershipId: string) => {
      return axiosMutationClient({
        url: WorkspaceMembershipIdRoute.build({
          id: workspaceId,
          membershipId
        }),
        method: 'delete'
      });
    },
    onSuccess: (_data, membershipId: string) =>
      queryClient.setQueryData<{
        pages: IListResponse<IWorkspaceMembership>[];
        pageParams: number;
      }>(queryKey, state => {
        if (!state) return { pages: [], pageParams: 0 };
        const newPages = state.pages.map(page => ({
          collection: page.collection.filter(
            (val: any) => val.id !== membershipId
          ),
          total: page.total - 1
        }));

        return {
          pages: newPages,
          pageParams: state?.pageParams
        };
      })
  });

  return removeUserFromOrganizationMutation;
};

export const addUsersToWorkspace = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-memberships', { workspaceId }];

  const mutation = useMutation({
    mutationFn: ({
      members,
      emailNotify
    }: {
      members: Omit<
        IWorkspaceMemberPayload,
        'workspaceId' | 'organizationId' | 'emailNotify'
      >[];
      emailNotify: boolean;
    }) =>
      axiosMutationClient<IAddWorkspaceMembershipResponse>({
        url: WorkspaceMembershipsRoute.build({ id: workspaceId }),
        payload: { members, emailNotify }
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

export const grantOrgAdminToUser = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-memberships', { workspaceId }];

  const mutation = useMutation({
    mutationFn: (email: string) =>
      axiosMutationClient<{ message: string }>({
        url: WorkspaceGrantOrgAdminRoute.build({ id: workspaceId }),
        payload: { email }
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

export const revertOrgAdminFromUser = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-memberships', { workspaceId }];

  const mutation = useMutation({
    mutationFn: (email: string) =>
      axiosMutationClient<{ message: string }>({
        url: WorkspaceRevertOrgAdminRoute.build({ id: workspaceId }),
        payload: { email }
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

export const RunImageGenerationCheck = (submissionId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-integrity', { submissionId }];
  const mutation = useMutation({
    mutationFn: (payload: IRunImageGenerationCheckPayload) =>
      axiosMutationClient<IRunImageGenerationCheckResponse>({
        url: '/run-check/imagetwin',
        payload
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

export const RunSelectedCheck = (submissionId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-integrity', { submissionId }];
  const mutation = useMutation({
    mutationFn: (payload: IIntegrityCheckManualRunPayload) =>
      axiosMutationClient<IIntegrityCheckManualRunResponse>({
        url: 'check-result/run/checks',
        payload
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

/**
 * Runs the checks configured for a role against a submission still in draft.
 *
 * The service refuses a second run once round 0 has results
 * (`BadRequest('Integrity checks already run for this submission')`), so the
 * caller's "Re-Run Checks" surfaces that message until the service allows it.
 */
export const RunDraftIntegrityChecks = (
  submissionId: string,
  role: IRoleType
) => {
  const queryClient = useQueryClient();
  const queryKey = ['draft-integrity-checks', { submissionId, role }];
  const mutation = useMutation({
    mutationFn: () =>
      axiosMutationClient<IIntegrityCheckManualRunResponse>({
        url: 'check-result/run/draft-checks',
        payload: { submissionId, role }
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

export const useAddJournalArticleType = (journalId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleTypeId }: { articleTypeId: string }) =>
      axiosMutationClient({
        url: `/submission-contexts/${journalId}/article-types`,
        payload: { articleTypeId },
        method: 'post'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['journalArticleTypes', journalId]
      });

      queryClient.invalidateQueries({
        queryKey: ['availableArticleTypes', journalId]
      });
    }
  });
};

export const useOrgArticleType = (orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['organization-article-types', { orgId }];

  const createArticleTypeMutation = useMutation({
    mutationFn: ({ name }: { name: string; id?: string }) =>
      axiosMutationClient<{ items: IArticleType[] }>({
        url: OrganizationArticleTypesRoute.build({ id: orgId }),
        payload: { name }
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return createArticleTypeMutation;
};

export const createWorkspaceTag = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-tags'];

  const createTagMutation = useMutation({
    mutationFn: ({
      name,
      description,
      usage,
      submissionContextId,
      tagCategoryId
    }: {
      name: string;
      description: string;
      usage: string;
      submissionContextId?: string;
      tagCategoryId?: string;
    }) =>
      axiosMutationClient<{ response: ITags }>({
        url: WorkspaceTagsRoute.build({ id: workspaceId }),
        payload: {
          name,
          description,
          usage,
          submissionContextId,
          tagCategoryId
        },
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return createTagMutation;
};

export const updateWorkspaceTag = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-tags'];

  const updateTagMutation = useMutation({
    mutationFn: ({
      name,
      description,
      usage,
      tagId,
      submissionContextId,
      tagCategoryId
    }: {
      name: string;
      description: string;
      usage: string;
      tagId: string;
      submissionContextId?: string;
      tagCategoryId?: string;
    }) =>
      axiosMutationClient<{ response: ITags }>({
        url: WorkspaceTagRoute.build({ id: workspaceId, tagId }),
        payload: {
          name,
          description,
          usage,
          submissionContextId,
          tagCategoryId
        },
        method: 'put'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return updateTagMutation;
};

export const updateEditorTag = (submissionId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['editor-tags', { submissionId: submissionId }];

  const updateTagMutation = useMutation({
    mutationFn: (payload: {
      deleteList: Array<{ id: string; name: string }>;
      selectedList: Array<{ id: string; name: string }>;
      submissionContextId: string;
    }) =>
      axiosMutationClient<ITags[]>({
        url: `submissions/${submissionId}/tags`,
        payload,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return updateTagMutation;
};

export const removeWorkspaceTag = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-tags'];

  const removeTagMutation = useMutation({
    mutationFn: (id: string) =>
      axiosMutationClient<{ items: ITags }>({
        url: WorkspaceTagRoute.build({ id: workspaceId, tagId: id }),
        method: 'delete'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return removeTagMutation;
};
export const saveSubmissionFilter = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-filter', { workspaceId }];

  const saveSubmissionFilterMutation = useMutation({
    mutationFn: ({
      filters,
      name
    }: {
      filters: IWorkspaceSubmissionFilter;
      name: string;
    }) =>
      axiosMutationClient<{ response: ISavedFilters }>({
        url: WorkspaceSavedFiltersRoute.build({ id: workspaceId }),
        payload: { ...filters, name },
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return saveSubmissionFilterMutation;
};
export const removeSaveFilters = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-filter', { workspaceId }];

  const removeTagMutation = useMutation({
    mutationFn: (id: string) =>
      axiosMutationClient<{ items: ITags }>({
        url: WorkspaceSavedFilterRoute.build({ id: workspaceId, sfid: id }),
        method: 'delete'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return removeTagMutation;
};
// Saving or deleting a draft moves a submission between the My Submissions
// tabs without dispatching a command, so the submission success callback in
// commands.ts never runs for it - the counts have to be refreshed here.
function invalidateSubmissionTotals(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [MY_SUBMISSIONS_TOTALS_QUERY_KEY]
  });
  queryClient.invalidateQueries({
    queryKey: [USER_ACTIVITY_TOTALS_QUERY_KEY]
  });
}

export const useDraftSubmission = () => {
  const queryClient = useQueryClient();
  const queryKey = ['my-submissions'];

  const createDraftSubmissionMutation = useMutation({
    mutationFn: (submissionDraftPayload: ISubmissionDraft) =>
      axiosMutationClient({
        url: `submission-contexts/${submissionDraftPayload.submissionContextId}/submissions/${submissionDraftPayload.submissionId}/draft`,
        payload: submissionDraftPayload,
        method: 'post'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      invalidateSubmissionTotals(queryClient);
    }
  });

  return createDraftSubmissionMutation;
};

export const removeDraftSubmission = () => {
  const queryClient = useQueryClient();
  const queryKey = ['my-submissions'];

  const removeDraftSubmissionMutation = useMutation({
    mutationFn: (payload: {
      submissionContextId: string;
      submissionId: string;
    }) =>
      axiosMutationClient({
        url: `submission-contexts/${payload.submissionContextId}/submissions/${payload.submissionId}/draft`,
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      invalidateSubmissionTotals(queryClient);
    }
  });

  return removeDraftSubmissionMutation;
};

export const useGenerateConsolidatedPdf = () => {
  const queryClient = useQueryClient();
  const queryKey = ['my-submissions'];

  const generateConsolidatedPdfMutation = useMutation({
    mutationFn: (payload: {
      submissionContextId: string;
      submissionId: string;
      rerun?: boolean;
    }) =>
      axiosMutationClient({
        url: `submission-contexts/${payload.submissionContextId}/submissions/${
          payload.submissionId
        }/draft/consolidated-pdf${payload.rerun ? '?rerun=true' : ''}`,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return generateConsolidatedPdfMutation;
};

export const useGenerateRevisionConsolidatedPdf = () => {
  const generateConsolidatedPdfMutation = useMutation({
    mutationFn: (payload: {
      submissionContextId: string;
      submissionId: string;
      rerun?: boolean;
    }) =>
      axiosMutationClient({
        url: `submission-contexts/${payload.submissionContextId}/submissions/${
          payload.submissionId
        }/revision/consolidated-pdf${payload.rerun ? '?rerun=true' : ''}`,
        method: 'post'
      })
  });

  return generateConsolidatedPdfMutation;
};

export const useFormDraft = () => {
  const queryClient = useQueryClient();
  const queryKey = ['review-list-totals'];

  const createDraftFormMutation = useMutation({
    mutationFn: (formDraftPayload: IFormDraft) =>
      axiosMutationClient({
        url: FormDraftRoute.build({ entityId: formDraftPayload.entityId }),
        payload: formDraftPayload,
        method: 'put'
      }),
    onSuccess: (_res, payload) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: [
          'form-draft',
          payload.entityId,
          payload.entityType,
          payload.formType,
          payload.phase
        ]
      });
    }
  });

  return createDraftFormMutation;
};

export const useEarlyAssessmentFormDraft = () => {
  const queryClient = useQueryClient();
  const queryKey = ['review-list-totals'];

  const createDraftFormMutation = useMutation({
    mutationFn: (formDraftPayload: IFormDraft) => {
      const { entityId, entityType, formType } = formDraftPayload;

      const queryParams = new URLSearchParams({
        entityId,
        entityType,
        formType
      }).toString();

      const url = `/form-drafts/${entityId}?${queryParams}`;

      return axiosMutationClient({
        url,
        payload: formDraftPayload,
        method: 'put'
      });
    },
    onSuccess: (_res, payload) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: [
          'form-draft',
          payload.entityId,
          payload.entityType,
          payload.formType,
          payload.phase
        ]
      });
    }
  });

  return createDraftFormMutation;
};

export const useUpdateReviewAdditionalInformation = (id: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['review', { id }];
  const updateReviewAdditionalInfoMutation = useMutation({
    mutationFn: (additionalInformationPayload: IReviewAdditionalInformation) =>
      axiosMutationClient({
        url: `reviews/${id}/additional-information`,
        payload: additionalInformationPayload,
        method: 'put'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return updateReviewAdditionalInfoMutation;
};

export const updateCoReviewDeclineReason = (id: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['review', { id }];
  const updateCoReviewDeclineReasonMutation = useMutation({
    mutationFn: (payload: { declineReason: string }) =>
      axiosMutationClient({
        url: CoReviewDeclineReasonRoute.build({ id }),
        payload,
        method: 'put'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return updateCoReviewDeclineReasonMutation;
};

export const useUpdateTransferNetworkConfig = (journalId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['transfer-network-config', { journalId }];

  const updateTransferNetworkConfigMutation = useMutation({
    mutationFn: (payload: { add: string[]; remove: string[] }) =>
      axiosMutationClient({
        url: TransferNetworkConfigRoute.build({ ctxId: journalId }),
        payload,
        method: 'patch'
      }),
    onSuccess: (res, payload) => {
      if (res?.isSuccess) {
        queryClient.setQueryData<IGetTransferNetworkConfigResponse>(
          queryKey,
          state => {
            if (!state) return [];

            return state.map(item => ({
              ...item,
              enabled: payload.add.includes(item.id)
                ? true
                : payload.remove.includes(item.id)
                ? false
                : item.enabled
            }));
          }
        );
      }
    }
  });

  return updateTransferNetworkConfigMutation;
};

export const createFocusIssue = (id: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['focus-issue', { id }];
  const createFocusIssueMutation = useMutation({
    mutationFn: (formData: IFocusIssueFormData & { status: string }) =>
      axiosMutationClient({
        url: `submission-contexts/${id}/focus-issues`,
        payload: formData,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return createFocusIssueMutation;
};

export const updateFocusIssue = (id: string, focusIssueId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['focus-issue', { id }];
  const updateFocusIssueMutation = useMutation({
    mutationFn: (formData: IFocusIssueFormData & { status: string }) =>
      axiosMutationClient({
        url: `submission-contexts/${id}/focus-issues/${focusIssueId}`,
        payload: formData,
        method: 'put'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return updateFocusIssueMutation;
};

export const updateFocusIssueStatus = (id: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['focus-issue', { id }];
  const updateFocusIssueStatusMutation = useMutation({
    mutationFn: ({
      status,
      focusIssueId
    }: {
      status: string;
      focusIssueId: string;
    }) =>
      axiosMutationClient({
        url: `submission-contexts/${id}/focus-issues/${focusIssueId}/update-status`,
        payload: { status },
        method: 'put'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return updateFocusIssueStatusMutation;
};

export const updateFocusIssueGuidelines = (id: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-context', id];
  const updateFocusIssueGuidelinesMutation = useMutation({
    mutationFn: (guidelineLink: string) =>
      axiosMutationClient({
        url: `submission-contexts/${id}/focus-issues/guidelines-link`,
        payload: { guidelineLink },
        method: 'put'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return updateFocusIssueGuidelinesMutation;
};

export const updateUserTag = (orgId: string, userId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-author-profile', { orgId, userId }];

  const updateTagMutation = useMutation({
    mutationFn: (payload: { tags: Array<string> }) =>
      axiosMutationClient<string[]>({
        url: `organizations/${orgId}/workspace-profiles/tags/${userId}`,
        payload,
        method: 'post'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['users-list'] });
    }
  });

  return updateTagMutation;
};

export const useAdjudicationFormDraft = () => {
  const queryClient = useQueryClient();
  const queryKey = ['adjudication-list-totals'];

  const createDraftFormMutation = useMutation({
    mutationFn: (formDraftPayload: IFormDraft) => {
      const { entityId, entityType, formType } = formDraftPayload;

      const queryParams = new URLSearchParams({
        entityId,
        entityType,
        formType
      }).toString();

      const url = `/form-drafts/${entityId}?${queryParams}`;

      return axiosMutationClient({
        url,
        payload: formDraftPayload,
        method: 'put'
      });
    },
    onSuccess: (_res, payload) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: [
          'form-draft',
          payload.entityId,
          payload.entityType,
          payload.formType,
          payload.phase
        ]
      });
    }
  });

  return createDraftFormMutation;
};

export const useCheckCoReviewerEligibility = () => {
  const checkCoReviewerEligibilityMutation = useMutation(
    ({ reviewId, email }: { reviewId: string; email: string }) =>
      checkCoReviewerEligibility(reviewId, email)
  );

  return checkCoReviewerEligibilityMutation;
};

export const useInviteCoReviewer = (
  submissionId: string,
  submissionContextId: string,
  organizationId: string
) => {
  const queryClient = useQueryClient();
  const inviteCoReviewerMutation = useMutation({
    mutationFn: (
      payload: Omit<
        IInviteCoReviewerCommand,
        | 'type'
        | 'submissionId'
        | 'submissionContextId'
        | 'formType'
        | 'organizationId'
      > & { formPayload: ICoReviewerInviteFormPayload }
    ) => {
      const command: IInviteCoReviewerCommand = {
        type: 'InviteCoReviewer',
        submissionId,
        organizationId,
        submissionContextId,
        reviewRequestId: payload.reviewRequestId,
        userId: payload.userId,
        coReviewerId: payload.coReviewerId,
        formType: 'inviteCoReviewer',
        formPayload: payload.formPayload
      };
      return axiosInstance
        .post(`submission-contexts/${submissionContextId}/commands`, {
          command
        })
        .then(r => {
          if (r.data && r.data.error) {
            throw new Error(r.data.error);
          }
          return { ...r.data };
        })
        .catch(e => {
          throw e;
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['invite-co-reviewer', { id: submissionId }]
      });
    }
  });

  return inviteCoReviewerMutation;
};

export const useRemoveJournalMembership = (
  journalId: string,
  workspaceId: string
) => {
  const queryClient = useQueryClient();
  const queryKey = ['journal-memberships', { journalId }];

  const mutation = useMutation({
    mutationFn: (assignmentId: string) =>
      axiosMutationClient({
        url: `${JournalMembershipRoute.build({
          ctxId: journalId
        })}?assignmentId=${assignmentId}`,
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['journal-memberships-coc', { journalId }]
      });
      queryClient.invalidateQueries({
        queryKey: ['unassigned-members', workspaceId, journalId]
      });
    }
  });

  return mutation;
};

export const useUpdateJournalMembership = (journalId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['journal-memberships', { journalId }];

  const mutation = useMutation({
    mutationFn: (payload: IJournalMember) =>
      axiosMutationClient<string[]>({
        url: JournalMembershipRoute.build({ ctxId: journalId }),
        payload,
        method: 'patch'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['journal-memberships-coc', { journalId }]
      });
    }
  });

  return mutation;
};

export const useAddJournalMembership = (
  journalId: string,
  workspaceId: string
) => {
  const queryClient = useQueryClient();
  const queryKey = ['journal-memberships', { journalId }];

  const mutation = useMutation({
    mutationFn: ({
      payload,
      emailNotify
    }: {
      payload: IJournalMember[];
      emailNotify: boolean;
    }) =>
      axiosMutationClient<string[]>({
        url: JournalMembershipRoute.build({ ctxId: journalId }),
        payload,
        method: 'post',
        params: { emailNotify }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['journal-memberships-coc', { journalId }]
      });
      queryClient.invalidateQueries({
        queryKey: ['unassigned-members', workspaceId, journalId]
      });
    }
  });

  return mutation;
};

type IUpdateReminderConfigForJournal = z.infer<typeof reminderInDaysSchema>;
export const useUpdateReminderConfigsByJournalId = (
  organizationId: string,
  submissionContextId: string
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: IUpdateReminderConfigForJournal) =>
      axiosMutationClient<string[]>({
        url: ReminderConfigsRouteForJournals.build({
          id: organizationId,
          ctxId: submissionContextId
        }),
        payload,
        method: 'put'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'reminder-emails-config',
          organizationId,
          submissionContextId
        ]
      });
      queryClient.invalidateQueries({
        queryKey: ['reminder-emails-config', organizationId]
      });
    }
  });

  return mutation;
};

export const saveFilterByView = (view: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-filter', { view }];

  const saveFilterByViewMutation = useMutation({
    mutationFn: ({
      filters,
      name
    }: {
      filters:
        | IMySubmissionFilter
        | IMyReviewsFilters
        | IAdjudicationsListFilters
        | IEarlyAssessmentListFilters;
      name: string;
    }) =>
      axiosMutationClient<{ response: ISavedFilters }>({
        url: '/dashboard/saved-filters',
        payload: { ...filters, name, view },
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return saveFilterByViewMutation;
};

export const useRetrySubmissionExport = (submissionId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-export-history', { submissionId }];

  const mutation = useMutation({
    mutationFn: (payload: IVendorExportJobPayload[]) => {
      return axiosMutationClient({
        url: `/submission/${submissionId}/vendorExport/retry`,
        payload,
        method: 'post'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    retrySubmissionExport: mutation.mutate,
    retrySubmissionExportAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useCreateExportConfig = (ctxId: string, orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-context-export-configs', { orgId }];

  const mutation = useMutation({
    mutationFn: (payload: exportConfigItem) => {
      return axiosMutationClient({
        url: `/organization/${orgId}/submission-context/${ctxId}/vendorExports/createExportConfiguration`,
        payload,
        method: 'post'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    createExportConfiguration: mutation.mutate,
    createExportConfigurationAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useValidateManuscripts = (ctxId: string, orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['validate-manuscripts', { ctxId }];

  const mutation = useMutation({
    mutationFn: (payload: ValidateManuscriptIdsPayload) => {
      return axiosMutationClient<ValidateManuscriptIdsResponse>({
        url: `/organization/${orgId}/submission-context/${ctxId}/vendorExports/validateManuscriptIds`,
        payload,
        method: 'post'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    validateManuscriptIds: mutation.mutate,
    validateManuscriptIdsAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};
export const useRetryBulkExports = (ctxId: string, orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-context-bulk-exports', { ctxId }];

  const mutation = useMutation({
    mutationFn: (payload: VendorExportRetryBulkExportPayload) => {
      return axiosMutationClient<VendorExportBulkRetryResponse>({
        url: `/organization/${orgId}/submission-context/${ctxId}/vendorExports/retryBulkExports`,
        payload,
        method: 'post'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    retryBulkExports: mutation.mutate,
    retryBulkExportsAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useRetryNotificationMail = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: { trackerId: string; email: string }) => {
      return axiosMutationClient<{ message: string }>({
        url: '/notification-manager/retry/mail',
        payload,
        method: 'post'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notification-webhook-events']
      });
      queryClient.invalidateQueries({
        queryKey: ['notification-webhook-event']
      });
    }
  });

  return {
    retryNotificationMail: mutation.mutate,
    retryNotificationMailAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useEditExportConfig = (
  ctxId: string,
  configId: string,
  orgId: string
) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-context-export-configs', { orgId }];

  const mutation = useMutation({
    mutationFn: (payload: exportConfigItem) => {
      return axiosMutationClient({
        url: `/organization/${orgId}/submission-context/${ctxId}/vendorExports/updateExportConfiguration/${configId}`,
        payload,
        method: 'put'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    editExportConfiguration: mutation.mutate,
    editExportConfigurationAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useDeleteExportConfig = (ctxId: string, orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-context-export-configs', { orgId }];

  const mutation = useMutation({
    mutationFn: (configId: string) => {
      return axiosMutationClient({
        url: `/organization/${orgId}/submission-context/${ctxId}/vendorExports/${configId}`,
        method: 'delete'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['submission-dashboard-config']
      });
    }
  });

  return {
    deleteExportConfiguration: mutation.mutate,
    deleteExportConfigurationAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useToggleExportConfig = (ctxId: string, orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-context-export-configs', { ctxId }];

  const mutation = useMutation({
    mutationFn: (payload: exportConfigItem) => {
      return axiosMutationClient({
        url: `/organization/${orgId}/submission-context/${ctxId}/vendorExports/toggleExportConfiguration/${payload?.id}`,
        payload,
        method: 'put'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    toggleExportConfiguration: mutation.mutate,
    toggleExportConfigurationAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const removeSavedFiltersByView = (view: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['submission-filter', { view }];

  const removeTagMutation = useMutation({
    mutationFn: (sfid: string) =>
      axiosMutationClient<{ items: ITags }>({
        url: DashboardSavedFilterRoute.build({ sfid: sfid }),
        method: 'delete'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });
  return removeTagMutation;
};

export const contactJournal = (ctxId: string, submissionId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['contact-publisher', { ctxId }];

  const contactJournalMutation = useMutation({
    mutationFn: (payload: ContactJournalPayload) =>
      axiosMutationClient({
        url: `/submission-contexts/${ctxId}/submissions/${submissionId}/contact-publisher`,
        payload,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });
  return contactJournalMutation;
};
export const generateReport = (reportsId: string, workspaceId: string) => {
  type GenerateReportSuccess = {
    isSuccess: true;
    data: ArrayBuffer;
    headers: Record<string, string | undefined>;
  };

  type GenerateReportFailure = {
    isSuccess: false;
    message: string | unknown;
  };

  type GenerateReportResult = GenerateReportSuccess | GenerateReportFailure;

  const queryClient = useQueryClient();
  const queryKey = ['generate-report', { reportsId }];

  const generateReportMutation = useMutation({
    mutationFn: async (payload: {
      reportId: string;
      requestSchema: ReportsRequestSchema;
      recipientsEmails?: string[];
      sendEmail: boolean;
      selectedColumns: string[];
    }): Promise<GenerateReportResult> => {
      try {
        const response = await axiosInstance.post(
          `/workspace/${workspaceId}/reports/generate/${
            reportsId || payload.reportId
          }`,
          payload,
          { responseType: 'arraybuffer' }
        );
        const headers: Record<string, string | undefined> = {};
        Object.entries(response.headers ?? {}).forEach(([key, value]) => {
          headers[key.toLowerCase()] =
            typeof value === 'string' ? value : String(value);
        });

        return {
          isSuccess: true,
          data: response.data,
          headers
        };
      } catch (e: any) {
        let errorMessage = 'Failed to generate report';
        try {
          const data = e?.response?.data;
          if (data instanceof ArrayBuffer) {
            const decoded = new TextDecoder().decode(data);
            try {
              const parsed = JSON.parse(decoded);
              errorMessage = parsed.message || decoded;
            } catch {
              errorMessage = decoded;
            }
          } else {
            errorMessage = data ?? e.message;
          }
        } catch {
          errorMessage = e.message;
        }

        return {
          isSuccess: false,
          message: errorMessage
        };
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });
  return {
    generateReport: generateReportMutation.mutate,
    generateReportAsync: generateReportMutation.mutateAsync,
    isLoading: generateReportMutation.isPending,
    isError: generateReportMutation.isError,
    error: generateReportMutation.error
  };
};

export type WorkspaceAnalyticsOverviewPayload = {
  analyticsType: 'chart' | 'stats';
  requestSchema: Record<string, string | string[]>;
  chartId?: string;
  statsId?: string;
  selectedTabId: string;
};

export type WorkspaceAnalyticsOverviewResponse = Record<string, unknown>;

export const useWorkspaceAnalyticsOverviewMutation = (workspaceId: string) => {
  const mutation = useMutation({
    mutationFn: (payload: WorkspaceAnalyticsOverviewPayload) =>
      axiosMutationClient<WorkspaceAnalyticsOverviewResponse>({
        url: `/workspace/${workspaceId}/analytics/overview`,
        payload
      })
  });

  return {
    fetchWorkspaceAnalyticsOverview: mutation.mutate,
    fetchWorkspaceAnalyticsOverviewAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error
  };
};

export const useCreateTagCategory = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['tag-category-list', { workspaceId }];

  const mutation = useMutation({
    mutationFn: (payload: ITagCategory) =>
      axiosMutationClient({
        url: `/workspaces/${workspaceId}/categories`,
        payload,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

export const useUpdateTagCategory = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['tag-category-list', { workspaceId }];

  const mutation = useMutation({
    mutationFn: ({
      categoryId,
      payload
    }: {
      categoryId: string;
      payload: ITagCategory;
    }) =>
      axiosMutationClient({
        url: `/workspaces/${workspaceId}/categories/${categoryId}`,
        payload,
        method: 'put'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
};

export const useDeleteTagCategory = (workspaceId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['tag-category-list', { workspaceId }];

  const mutation = useMutation({
    mutationFn: (categoryId: string) =>
      axiosMutationClient({
        url: `/workspaces/${workspaceId}/categories/${categoryId}`,
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
};

export const useUpdateArticleTypeConfig = (
  submissionContextId: string,
  articleTypeId: string
) => {
  const queryClient = useQueryClient();
  const queryKey = ['document-schema', articleTypeId, submissionContextId];

  const mutation = useMutation({
    mutationFn: (payload: { fields: Record<string, FormFieldDefinition> }) =>
      axiosMutationClient<{
        documentSchema?: unknown;
      }>({
        url: `/submission-contexts/${submissionContextId}/article-types/${articleTypeId}/document-schema`,
        payload,
        method: 'patch'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return mutation;
};

export const useDeleteArticleType = (submissionContextId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleTypeId: string) =>
      axiosMutationClient({
        url: `/submission-contexts/${submissionContextId}/article-types/${articleTypeId}`,
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['journalArticleTypes']
      });
      queryClient.invalidateQueries({
        queryKey: ['availableArticleTypes', submissionContextId]
      });
    }
  });
};

export type MergeAccountsPayload = {
  primaryUserId: string;
  secondaryUserIds: string[];
  orcidSourceUserId: string;
  affiliationSourceUserId: string;
  tagSourceUserIds: string[];
};

export type MergeAccountsResponse = {
  mergeId: string;
  submissionsCount: number;
  draftSubmissionsCount: number;
  archiveSubmissionsCount: number;
  totalReviewsCount: number;
  inProgressReviewCount: number;
  completedReviewCount: number;
  invitedReviewCount: number;
};

export const useWorkspaceMergeAccounts = (workspaceId: string) =>
  useMutation({
    mutationFn: (payload: MergeAccountsPayload) =>
      axiosMutationClient<MergeAccountsResponse>({
        url: WorkspaceMergeAccountsRoute.build({ id: workspaceId }),
        payload
      })
  });

export const useWorkspaceMergeAccountsUndo = (workspaceId: string) =>
  useMutation({
    mutationFn: (mergeId: string) =>
      axiosMutationClient({
        url: WorkspaceMergeAccountsUndoRoute.build({ id: workspaceId }),
        payload: { mergeId }
      })
  });
export const contactUs = (orgId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['contact-us', { orgId }];

  const contactUsMutation = useMutation({
    mutationFn: (payload: ContactUsPayload) =>
      axiosMutationClient({
        url: `${orgId}/contact-us`,
        payload,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });
  return contactUsMutation;
};

export const contactUsUnverified = () => {
  const queryClient = useQueryClient();
  const queryKey = ['contact-us-unverified'];

  const contactUsUnverifiedMutation = useMutation({
    mutationFn: (payload: ContactUsPayload) =>
      axiosMutationClient({
        url: `/user/contact-us`,
        payload,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });
  return contactUsUnverifiedMutation;
};

export const AcknowledgeReport = (submissionId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['uad-reports', { submissionId }];

  const AcknowledgeReportMutation = useMutation({
    mutationFn: (payload: IUADReportAcknowledgmentPayload) =>
      axiosMutationClient({
        url: `/submission/${submissionId}/acknowledge/uad-reports`,
        payload: payload,
        method: 'post'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey })
  });

  return AcknowledgeReportMutation;
};

export type ICreateJournalPayload = {
  type: 'journal';
  organizationId: string;
  workspaceId: string;
  data: {
    name: string;
    shortname: string;
    description: string;
    introduction: string;
  };
  config: {
    issn: {
      online: string;
      print: string;
    };
    url: string;
    logoUrl: string;
  };
};

export type ICreateJournalResponse = {
  id: string;
  message: string;
  [key: string]: any;
};

export const useCreateJournalMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ICreateJournalPayload) => {
      return axiosInstance
        .post<ICreateJournalResponse>('/submission-contexts', payload)
        .then(r => ({
          ...r.data,
          isSuccess: true as const
        }));
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['workspace-journals', { workspaceId: variables.workspaceId }]
      });
    }
  });
};

export function notifyAuthors(submissionId: string) {
  return axiosInstance
    .post<{
      success: boolean;
    }>(`/submission/${submissionId}//send-notification`, {})
    .then(r => r.data);
}

export const useSaveJournalConfigurationBuckets = (
  submissionContextId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      role: string;
      bucketName: string;
      subBucketNames: string[];
    }) =>
      axiosMutationClient({
        url: JournalConfigurationBucketsRoute.build({
          ctxId: submissionContextId
        }),
        payload,
        method: 'post'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['journal-configuration-buckets', submissionContextId]
      });
    }
  });
};

export const useDeleteJournalConfigurationBucket = (
  submissionContextId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bucketId }: { bucketId: string }) =>
      axiosMutationClient({
        url: `${JournalConfigurationBucketsRoute.build({
          ctxId: submissionContextId
        })}?bucketId=${bucketId}`,
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['journal-configuration-buckets', submissionContextId]
      });
    }
  });
};

export type SaveChecklistPayload = {
  id?: string;
  phase: string;
  articleWorkflowId: string;
  formFields: IEditorialFormField[];
  schema: Record<string, unknown>;
};

export const useSaveJournalChecklists = (submissionContextId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveChecklistPayload[]) =>
      axiosMutationClient({
        url: JournalConfigurationArticleChecklistRoute.build({
          ctxId: submissionContextId
        }),
        payload,
        method: 'post'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['journal-configuration-checklists', submissionContextId]
      });
    }
  });
};

export const useSaveJournalDecisionTemplate = (submissionContextId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      templateId?: string;
      text: string;
      label: string;
      type: string;
      subtype?: string;
      templateType: string;
    }) =>
      axiosMutationClient({
        url: JournalConfigurationDecisionTemplatesRoute.build({
          ctxId: submissionContextId
        }),
        payload,
        method: 'post'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'journal-configuration-decision-templates',
          submissionContextId
        ]
      });
    }
  });
};

export const useDeleteJournalDecisionTemplate = (
  submissionContextId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId }: { templateId: string }) =>
      axiosMutationClient({
        url: `${JournalConfigurationDecisionTemplatesRoute.build({
          ctxId: submissionContextId
        })}?templateId=${templateId}`,
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'journal-configuration-decision-templates',
          submissionContextId
        ]
      });
    }
  });
};

export const useCreateReviewerSearchUser = (ctxId: string) => {
  return useMutation({
    mutationFn: (data: Record<string, string | string[]>) =>
      axiosMutationClient({
        url: `/submission-contexts/${ctxId}/reviews/lexicon/reviewer`,
        payload: { data }
      })
  });
};

export const useUpdateReviewerSearchUser = (ctxId: string) => {
  return useMutation({
    mutationFn: ({
      email,
      data
    }: {
      email: string;
      data: Record<string, string | string[]>;
    }) =>
      axiosMutationClient({
        url: `/submission-contexts/${ctxId}/reviews/lexicon/reviewer`,
        method: 'put',
        payload: { email, data }
      })
  });
};

export const useDeleteReviewerSearchUser = (ctxId: string) => {
  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      axiosMutationClient({
        url: `/submission-contexts/${ctxId}/reviews/lexicon/reviewer`,
        method: 'delete',
        payload: { email }
      })
  });
};
